import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { connect } from "react-redux";
import i18n from "I18n";

import ReactHookFormSwitch from "components/reactHookFormComponents/ReactHookFormSwitch";
import ImageUploader from "components/base/ImageUploader";
import { errorMessage, ReactHookFormErrorMessage } from "components/base/ErrorFieldMessage";
import { LanguageConfig } from "config/CustomEnums";
import { insertKeyToAllLanguages } from "utils";

const StampIconUploader = ({
  languages,
  language,
  focusChange,
}) => {
  const { formState: { errors }, watch, setValue, getValues, control } = useFormContext();

  if (language !== LanguageConfig.english) {
    return <></>
  }

  const useUploadStampIcons = watch('useUploadStampIcons');
  const emptyStampSlot = watch(`stampCampaignTypeEmptySlotStampImage`) || '';
  const collectedStamp = watch(`stampCampaignTypeAcquiredStampImage`) || '';
  const emptyGiftStampSlot = watch(`stampCampaignTypeEmptyGiftSlotImage`) || '';
  const collectedGift = watch(`stampCampaignTypeCollectedGiftImage`) || '';
  const emptyStampSlotPhoto = [emptyStampSlot]?.filter((item) => item);
  const collectedStampPhoto = [collectedStamp]?.filter((item) => item);
  const emptyGiftStampSlotPhoto = [emptyGiftStampSlot]?.filter((item) => item);
  const collectedGiftPhoto = [collectedGift]?.filter((item) => item);

  const imageUploaderConfig = [
    {
      i18nTitlePk: 'emptyStampSlot',
      i18nSuggestPk: 'emptyStampSlotSuggest',
      disabled: !useUploadStampIcons,
      allowJson: false,
      onImageStateChange: (newState) => {
        setValue('stampCampaignTypeEmptySlotStampImage', newState[0], {
          shouldDirty: true,
        });
      },
      errorMessage: errorMessage(
        errors,
        'stampCampaignTypeEmptySlotStampImage',
      ),
      show: language === LanguageConfig.english,
      images: emptyStampSlotPhoto,
      name: 'stampCampaignTypeEmptySlotStampImage',
    },
    {
      i18nTitlePk: 'collectedStamp',
      i18nSuggestPk: 'collectedStampSuggest',
      disabled: !useUploadStampIcons ,
      allowJson: true,
      onImageStateChange: (newState) => {
        setValue('stampCampaignTypeAcquiredStampImage', newState[0], {
          shouldDirty: true,
        });
      },
      errorMessage: errorMessage(errors, 'stampCampaignTypeAcquiredStampImage'),
      show: language === LanguageConfig.english,
      images: collectedStampPhoto,
      name: 'stampCampaignTypeAcquiredStampImage',
    },
    {
      i18nTitlePk: 'emptyGiftStampSlot',
      i18nSuggestPk: 'emptyGiftStampSlotSuggest',
      disabled: !useUploadStampIcons ,
      allowJson: false,
      onImageStateChange: (newState) => {
        setValue('stampCampaignTypeEmptyGiftSlotImage', newState[0], {
          shouldDirty: true,
        });
      },
      errorMessage: errorMessage(errors, 'stampCampaignTypeEmptyGiftSlotImage'),
      show: language === LanguageConfig.english,
      images: emptyGiftStampSlotPhoto,
      name: 'stampCampaignTypeEmptyGiftSlotImage',
    },
    {
      i18nTitlePk: 'collectedGift',
      i18nSuggestPk: 'collectedGiftSuggest',
      disabled: !useUploadStampIcons ,
      allowJson: true,
      onImageStateChange: (newState) => {
        setValue(`stampCampaignTypeCollectedGiftImage`, newState[0], {
          shouldDirty: true,
        });
      },
      errorMessage: errorMessage(errors, 'stampCampaignTypeCollectedGiftImage'),
      show: language === LanguageConfig.english,
      images: collectedGiftPhoto,
      name: 'stampCampaignTypeCollectedGiftImage',
    },
  ];

  const imageUploaderWithLabelComponent = (config) => (
    <>
      {config.show && (
        <>
          <label className="create-section-label create-section-label-bottom-space">
            {i18n.t(config.i18nTitlePk, { locale: language })}
          </label>
          <label className="create-campaign-cover-photo-reminder">
            {i18n.t(config.i18nSuggestPk, { locale: language })}
          </label>
          <Controller
            control={control}
            name={config.name}
            render={() => (
              <ImageUploader
                minWidth={120}
                minHeight={120}
                images={config.images}
                disabled={config.disabled}
                allowJson={config.allowJson}
                onImageStateChange={config.onImageStateChange}
                aspect={1}
                maxImageNum={1}
                uploadImageClicked={() => {
                  focusChange('stampIcons');
                }}
                language={language}
                sizeLimit={2}
              />
            )}
          />
          <ReactHookFormErrorMessage errors={errors} id={config.name} />
        </>
      )}
    </>
  );

  return (
    <>
      <ReactHookFormSwitch
        title={'Upload stamp icons'}
        valueFieldName={"useUploadStampIcons"}
        bottomDescription={"If off, the default animation will be applied."}
        extraActionOnChange={(value) => {
          if (value) {
            insertKeyToAllLanguages({
              languages,
              key: 'collectedGiftPhoto',
              value: collectedGiftPhoto,
              setValue,
              getValues,
            });
          }
        }}
      />
      {useUploadStampIcons && (
        <>
          {imageUploaderConfig.map((config) =>
            imageUploaderWithLabelComponent(config),
          )}
        </>
      )}
    </>)
}

const mapPropsToState = (state) => ({
  languages: state.language.allList,
});

export default connect(mapPropsToState)(StampIconUploader);